import {
  Alert,
  Button,
  Snackbar,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BackendBaseUrl, AuthContext, ORANGE } from "./global_varibles";
import { useContext, useState } from "react";
import HeaderAppBar from "./appbar";
import type { AlertColor } from "@mui/material";
import { Lock, User, Eye, EyeOff } from "lucide-react";

interface UserDetails {
  id: string;
  name?: string;
  pick?: string;
  phone?: string;
}

interface AuthCtx {
  login: () => void;
  UpdateUser: (user: UserDetails) => void;
}

interface BackendResponse {
  status?: string;
  message?: string;
  user_details?: UserDetails;
}

interface FormData {
  id: string;
  password: string;
}

function SignInForm() {
  const { login, UpdateUser } = useContext(AuthContext as unknown as React.Context<AuthCtx>);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<AlertColor>("success");
  const [open, setOpen] = useState(false);
  const [form_data, updateformdata] = useState<FormData>({ id: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => setOpen(false);

  const handleFormData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    updateformdata((prev) => ({ ...prev, [id]: value }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const verifyTheUser = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!form_data.id || !form_data.password) {
      setMessageType("error");
      setMessage("Enter credentials");
      setOpen(true);
      return;
    }
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        id: form_data.id,
        password: form_data.password,
      });
      const backendrout = `${BackendBaseUrl}mng-manager?${queryParams.toString()}`;
      const response = await fetch(backendrout, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const response_data = (await response.json()) as BackendResponse;
      const respMessage = response_data.message || (response.ok ? "Success" : "Failed");
      if (response.ok && response_data.user_details) {
        UpdateUser(response_data.user_details);
        login();
        setMessageType("success");
        setMessage(respMessage);
        setOpen(true);
        navigate("/");
      } else {
        setMessageType("error");
        setMessage(respMessage);
        setOpen(true);
      }
    } catch (error) {
      setMessageType("error");
      setMessage("Internal Error");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4">
      {/* Dot Pattern Background */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Login Card */}
        <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-2xl shadow-black/5 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-orange-500 to-orange-600"></div>
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-500 font-medium">Sign in to your MuftyCare account</p>
          </div>

          {/* Login Form */}
          <form className="space-y-6">
            {/* User ID Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-gray-700 text-sm font-bold">
                <User className="w-4 h-4 text-orange-500" />
                User ID
              </label>
              <TextField
                id="id"
                type="text"
                fullWidth
                value={form_data.id}
                onChange={handleFormData}
                placeholder="Enter your user ID"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.08)',
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(249, 115, 22, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: ORANGE,
                    },
                    '& input': {
                      color: '#1a1a1a',
                      fontSize: '16px',
                      fontWeight: 600,
                      '&::placeholder': {
                        color: 'rgba(0, 0, 0, 0.4)',
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-gray-700 text-sm font-bold">
                <Lock className="w-4 h-4 text-orange-500" />
                Password
              </label>
              <div className="relative">
                <TextField
                  id="password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={form_data.password}
                  onChange={handleFormData}
                  placeholder="Enter your password"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.08)',
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(249, 115, 22, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: ORANGE,
                      },
                      '& input': {
                        color: '#1a1a1a',
                        fontSize: '16px',
                        fontWeight: 600,
                        paddingRight: '3rem',
                        '&::placeholder': {
                          color: 'rgba(0, 0, 0, 0.4)',
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-500 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              onClick={verifyTheUser}
              disabled={loading}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: '16px',
                textTransform: 'none',
                border: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: 'rgba(249, 115, 22, 0.5)',
                  transform: 'none',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Verifying...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Sign In
                </div>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">
              Don't have an account?{" "}
              <button 
                onClick={() => navigate("/contact")}
                className="text-orange-500 hover:text-orange-400 font-semibold transition-colors"
              >
                Contact Admin
              </button>
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 bg-orange-50 border border-orange-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-orange-500 mt-0.5" />
            <div>
              <p className="text-orange-700 font-bold text-sm">Secure Login</p>
              <p className="text-gray-600 text-xs mt-1 leading-relaxed">
                Your credentials are encrypted and secure. Contact your administrator if you've forgotten your login details.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ 
          top: "100px !important",
          '& .MuiSnackbarContent-root': {
            minWidth: '300px',
          }
        }}
      >
        <Alert 
          severity={messageType} 
          onClose={handleClose}
          sx={{
            background: messageType === 'success' 
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.9) 100%)'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)',
            color: 'white',
            borderRadius: '12px',
            border: `1px solid ${messageType === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            backdropFilter: 'blur(10px)',
            '& .MuiAlert-icon': {
              color: 'white',
            },
            '& .MuiAlert-action': {
              color: 'white',
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  );
}

function LogInPage() {
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <HeaderAppBar />
      <SignInForm />
    </div>
  );
}

export default LogInPage;
